/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement, useState, useEffect } from 'react'
import { Route, Switch, withRouter } from 'react-router-dom'
import Auth from '@aws-amplify/auth'
import { Spinner } from 'react-bootstrap'
import ReactGA from 'react-ga'
import moment from 'moment'
import { inject, observer } from 'mobx-react'

import PrivateRoute from './PrivateRoute'
import Init from './Init'
import Login from './Login'
import Register from './Login/Register'
import Signup from './Login/Signup'
import NewSignup from './Signup'
import NewConfirmation from './Signup/Confirmation'
import Swagger from './Swagger'
import ForgotPassword from './Login/ForgotPassword'
import ResetPassword from './Login/ResetPassword'
import { Sender, LoginStageStore } from './types'
import { getEnvironment, initOffice, loadOffice } from './office'
import config from '../config'
import Billing from './Signup/billing'
import ProspectManagement from './Signup/ProspectManagement'
import Writer from './Signup/Writer'
import Welcome from './Signup/Welcome'
import Recon from './Signup/Recon'
import Customer from './Signup/Customer'
import Congratulations from './Signup/Congratulations'
import Templates from './Signup/templates'
import Criteria from './Signup/criteria'
import Admin from '../pages/Admin'
import ContactRecon from '../pages/ContactRecon'
import MyCrewDetails from '../pages/MyCrewDetails'
import AdminReports from '../pages/AdminReports'
import AdminTemplates from '../pages/AdminTemplates'
import BulkContact from '../pages/BulkContact'
import AddSingleContact from '../pages/AddSingleContact'
import AdminSignature from '../pages/AdminSignature'
import DoNotEngage from '../pages/DoNotEngage'
import LinkEmail from '../pages/LinkEmail'

const App = (props): JSX.Element => {
  const [isAuthenticating, setIsAuthenticating] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(true)
  const [session, setSession] = useState<any>(null)
  const [emailUser, setEmailUser] = useState('')
  const [sender, setSender] = useState<Sender | null>(null)
  const { history, location, settings, prospects } = props

  const initializeReactGA = (): void => {
    const { googleAnalytics } = config

    if (!googleAnalytics.enabled) {
      return
    }
    const userSub = session?.getIdToken().payload?.sub

    ReactGA.set({ userId: userSub })
    ReactGA.pageview(window.location.origin + window.location.pathname)
  }

  const getUserInfo = async (): Promise<void> => {
    try {
      setIsAuthenticating(true)

      const tempUser: any = await Auth.currentAuthenticatedUser()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tempSession: any = await Auth.currentSession()
      setIsAuthenticating(false)
      setIsAuthenticated(true)
      if (tempUser !== null) {
        setEmailUser(tempUser?.attributes?.email || null)
        prospects?.setCurrentUserEmail(tempUser?.attributes?.email || null)
      }
      setSession(tempSession)
    } catch (e) {
      setIsAuthenticating(false)
      setIsAuthenticated(false)
      setSession(null)
    }
  }

  const setLoginStage = (loginStage: 'reset' | 'signup'): void => {
    localStorage.setItem(
      'loginStage',
      JSON.stringify({
        loginStage,
        lastModified: moment().toISOString(),
      }),
    )
  }

  const getLoginStage = (): LoginStageStore | null => {
    const loginStageFromStore = localStorage.getItem('loginStage')

    if (loginStageFromStore) {
      try {
        const loginStage: LoginStageStore = JSON.parse(loginStageFromStore)

        return loginStage
      } catch {
        return null
      }
    }

    return null
  }

  useEffect(() => {
    settings?.getSignatures()

    if (location.pathname === '/authorize') {
      history.push(`/settings/outlook-authorize${location.search}`)
    }

    const environment = getEnvironment()

    if (environment === 'outlook-desktop' || environment === 'outlook-web') {
      // Do all of this logic AFTER the Office.js script is loaded into the page
      loadOffice(async () => {
        /* eslint-disable no-underscore-dangle */
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        window.history.replaceState = window._historyCache.replaceState
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        window.history.pushState = window._historyCache.pushState
        /* eslint-disable no-underscore-dangle */
        await getUserInfo()

        // eslint-disable-next-line @typescript-eslint/unbound-method
        const isOfficeClient = await initOffice(history.push)

        settings?.setIsOfficeClient(isOfficeClient)
      })
    } else {
      settings?.setIsOfficeClient(false)
      getUserInfo()
    }
  }, [])

  useEffect(() => {
    initializeReactGA()
  }, [session, window.location.pathname])

  if (isAuthenticating) {
    return <Spinner animation="border" variant="light" />
  }

  return (
    <Switch>
      <Route
        path="/init/:recipient/:senderEmail/:senderName/:missionCompletedEmail?"
        render={(): ReactElement => <Init setSender={setSender} getLoginStage={getLoginStage} />}
      />
      <Route
        path="/login"
        render={(): ReactElement => (
          <Login
            setEmailUser={setEmailUser}
            setIsAuthenticated={setIsAuthenticated}
            isAuthenticated={isAuthenticated}
          />
        )}
      />
      <Route path="/register" component={Register} />
      <PrivateRoute path="/swagger" isAuthenticated={isAuthenticated} component={<Swagger />} />

      <Route
        path="/signup/:email?"
        render={(): ReactElement => <Signup setLoginStage={setLoginStage} />}
      />
      <Route
        path="/forgot-password"
        render={(): ReactElement => <ForgotPassword setLoginStage={setLoginStage} />}
      />
      <PrivateRoute
        path="/home/:email?"
        isAuthenticated={isAuthenticated}
        component={
          <Admin sender={sender} userEmail={emailUser} setIsAuthenticated={setIsAuthenticated} />
        }
      />
      <PrivateRoute
        path="/bulk-import"
        isAuthenticated={isAuthenticated}
        component={<BulkContact setIsAuthenticated={setIsAuthenticated} />}
      />
      <PrivateRoute
        path="/new-contact"
        isAuthenticated={isAuthenticated}
        component={
          <AddSingleContact emailUser={emailUser} setIsAuthenticated={setIsAuthenticated} />
        }
      />
      <PrivateRoute
        path="/recon"
        isAuthenticated={isAuthenticated}
        component={<ContactRecon setIsAuthenticated={setIsAuthenticated} />}
      />
      <PrivateRoute
        path="/crew-details"
        isAuthenticated={isAuthenticated}
        component={<MyCrewDetails setIsAuthenticated={setIsAuthenticated} />}
      />
      <PrivateRoute
        path="/authorize"
        isAuthenticated={isAuthenticated}
        component={<LinkEmail setIsAuthenticated={setIsAuthenticated} />}
      />
      <PrivateRoute
        path="/do-not-engage"
        isAuthenticated={isAuthenticated}
        component={<DoNotEngage setIsAuthenticated={setIsAuthenticated} />}
      />
      <PrivateRoute
        path="/reports"
        isAuthenticated={isAuthenticated}
        component={
          <AdminReports
            sender={sender}
            userEmail={emailUser}
            setIsAuthenticated={setIsAuthenticated}
          />
        }
      />
      <PrivateRoute
        path="/templates"
        isAuthenticated={isAuthenticated}
        component={<AdminTemplates setIsAuthenticated={setIsAuthenticated} />}
      />
      <PrivateRoute
        path="/signature"
        isAuthenticated={isAuthenticated}
        component={<AdminSignature setIsAuthenticated={setIsAuthenticated} />}
      />
      {/* This "/authorize" route is for the redirect URI that was set when Outlook auth was set up.
      This should be rendered when Outlook authenticates a user after a user clicks on the
      "Connect to Outlook" button. When this "/authorize" route is hit, it should redirect to
      "/settings/outlook-authorize", which will render the "Authorize" component. On render, the
      component will get the `?code=` query parameter and send it to the API as a grant, and then
      close the dialog (itself).

      The "to" property includes "...location" so that the query
      parameters that Outlook's authentication provides (like "code") will continue to the
      component that is being redirected to.

      For example, if Outlook redirects to
      "/authorize?code=12345", this redirect will redirect to
      "/settings/outlook-authorize?code=12345". If we didn't include the "...location", it'd just
      be "/settings/outlook-authorize" and no query parameters would be preserved. */}

      <Route path="/reset-password/:email" component={ResetPassword} />

      <Route path="/client/signup" render={(): ReactElement => <NewSignup />} />
      <Route path="/client/confirmation" render={(): ReactElement => <NewConfirmation />} />
      <Route path="/client/billing" render={(): ReactElement => <Billing />} />
      <Route path="/client/prospect" render={(): ReactElement => <ProspectManagement />} />
      <Route path="/client/writer" render={(): ReactElement => <Writer />} />
      <Route path="/client/welcome" render={(): ReactElement => <Welcome />} />
      <Route path="/client/recon" render={(): ReactElement => <Recon />} />
      <Route path="/client/customers" render={(): ReactElement => <Customer />} />
      <Route path="/client/congratulations" render={(): ReactElement => <Congratulations />} />
      <Route path="/client/templates" render={(): ReactElement => <Templates />} />
      <Route path="/client/criteria" render={(): ReactElement => <Criteria />} />
      <PrivateRoute
        path="*"
        isAuthenticated={isAuthenticated}
        component={
          <Admin sender={sender} userEmail={emailUser} setIsAuthenticated={setIsAuthenticated} />
        }
      />
    </Switch>
  )
}

export default inject('prospects', 'settings')(withRouter(observer(App)))
